import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, TThunkApi } from 'store/store';
import {
  killUserSessions,
  searchUser,
  selfUserInfo,
  unblockUser,
  updateUser,
} from 'api/MakhsanApi';

export interface IUserInfo {
  deleted: boolean;
  user_id: number;
  first_name: string;
  last_name: string;
  enabled: boolean;
  blocked: boolean;
  blockReason: string;
  email: string;
  phone: string;
  active2fa: boolean;
  hasPassword: boolean;
  validIps: string | undefined;
  timeout: number | undefined;
  roles: string[];
}

export interface IUserInfos {
  [key: number]: IUserInfo;
}

const initialState: IUserInfos = {};

const addOrUpdateUsersHandler = (
  state: IUserInfos,
  action: PayloadAction<{ userInfos: Array<IUserInfo> }>
) => {
  action.payload.userInfos.forEach((userInfo) => {
    state[userInfo.user_id] = userInfo;
  });
};

const updateUserRolesHandler = (
  state: IUserInfos,
  action: PayloadAction<{
    action: string;
    userId: string | number;
    role: string;
  }>
) => {
  if (action.payload.action === 'add') {
    state[action.payload.userId].roles = state[
      action.payload.userId
    ].roles.reduce(
      (current: Array<string>, item: string) => {
        current.push(item);

        return current;
      },
      [action.payload.role]
    );
  } else {
    state[action.payload.userId].roles = state[
      action.payload.userId
    ].roles.reduce((current: Array<string>, item: string) => {
      if (item !== action.payload.role) current.push(item);

      return current;
    }, []);
  }
};

export const makhsanSearchUserFn = async (
  {
    query,
    blocked,
    from,
    count,
  }: { query: string; blocked?: boolean; from?: number; count?: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return searchUser(store, query, blocked, from, count).then(
    ({ data: userInfos }) => {
      ThunkApi.dispatch(addOrUpdateUsers({ userInfos }));
      return Promise.resolve();
    }
  );
};
const makhsanSearchUser = createAsyncThunk(
  'makhsanUserInfo/makhsanSearchUser',
  makhsanSearchUserFn
);

export const makhsanSelfUserInfoFn = async (
  { userId }: { userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return selfUserInfo(store, userId).then(({ data: userInfo }) => {
    ThunkApi.dispatch(addOrUpdateUsers({ userInfos: [userInfo] }));
    return Promise.resolve({ userInfos: [userInfo] });
  });
};
const makhsanSelfUserInfo = createAsyncThunk(
  'makhsanUserInfo/makhsanSelfUserInfo',
  makhsanSelfUserInfoFn
);

export const makhsanUpdateUserFn = async (
  {
    deleted,
    firstName,
    lastName,
    password,
    active2fa,
    enable,
    validIps,
    timeout,
    userId,
  }: {
    deleted: boolean | null;
    firstName: string | null;
    lastName: string | null;
    password: string | null;
    active2fa: boolean | null;
    enable: boolean | null;
    validIps: string | undefined;
    timeout: number | undefined;
    userId: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return updateUser(
    store,
    deleted,
    firstName,
    lastName,
    password,
    active2fa,
    enable,
    validIps,
    timeout,
    userId
  ).then(({ data: userInfos }) => {
    ThunkApi.dispatch(addOrUpdateUsers({ userInfos: [userInfos] }));
    return Promise.resolve({
      userInfos: [userInfos],
    });
  });
};
const makhsanUpdateUser = createAsyncThunk(
  'makhsanUserInfo/makhsanUpdateUser',
  makhsanUpdateUserFn
);

export const makhsanUnblockUserFn = async (
  { userId }: { userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return unblockUser(store, userId).then(({ data: userInfos }) => {
    ThunkApi.dispatch(addOrUpdateUsers({ userInfos: [userInfos] }));
    return Promise.resolve({ userInfos: [userInfos] });
  });
};
const makhsanUnblockUser = createAsyncThunk(
  'makhsanUserInfo/makhsanUnblockUser',
  makhsanUnblockUserFn
);

export const makhsanKillUserSessionsFn = async (
  { userId }: { userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return killUserSessions(store, userId).then(() => {
    return Promise.resolve();
  });
};
const makhsanKillUserSessions = createAsyncThunk(
  'makhsanUserInfo/makhsanKillUserSessions',
  makhsanKillUserSessionsFn
);

const makhsanUserInfoSlice = createSlice({
  name: 'makhsanUserInfo',
  initialState: initialState,
  reducers: {
    addOrUpdateUsers: addOrUpdateUsersHandler,
    updateUserRoles: updateUserRolesHandler,
  },
});

export const { addOrUpdateUsers, updateUserRoles } =
  makhsanUserInfoSlice.actions;

export {
  makhsanSearchUser,
  makhsanSelfUserInfo,
  makhsanUpdateUser,
  makhsanUnblockUser,
  makhsanKillUserSessions,
};

export default makhsanUserInfoSlice.reducer;
